import { apiSlice } from '../apiSlice';
import { Vacancy, SaveVacancyDto, UpdateUserVacancyDto, UpdateStatusVacancyDto, UpdateVacancyCandidateDto, SearchVacancyDto, VacancyHistory } from '../../types/Vacancy';

const endpointUrl = '/vacancies';

function getVacancies() {
  return `${endpointUrl}`;
}

function getVacancy({ id }: { id: string }) {
  return `${endpointUrl}/${id}`;
}

function getVacancyHistory({ id }: { id: string }) {
  return `${endpointUrl}/${id}/history`;
}

function createVacancy(payload: SaveVacancyDto) {
  return {
    url: `${endpointUrl}`,
    method: 'POST',
    body: payload
  };
}

function updateVacancy({ id, ...payload }: { id: string } & SaveVacancyDto) {
  return {
    url: `${endpointUrl}/${id}`,
    method: 'PUT',
    body: payload
  };
}

function updateUserVacancy({ id, userId }: { id: string; userId: string | null }) {
  return {
    url: `${endpointUrl}/${id}/user`,
    method: 'PATCH',
    body: { userId }
  };
}

function updateStatusVacancy({ id, status }: { id: string; status: string }) {
  return {
    url: `${endpointUrl}/${id}/status`,
    method: 'PATCH',
    body: { status }
  };
}

function updateVacancyCandidate({ id, candidateId, candidateForms }: { id: string } & UpdateVacancyCandidateDto) {
  return {
    url: `${endpointUrl}/${id}/candidate`,
    method: 'PATCH',
    body: { candidateId, candidateForms }
  };
}

function searchVacancies(payload: SearchVacancyDto) {
  return {
    url: `${endpointUrl}/search`,
    method: 'POST',
    body: payload
  };
}

function uploadFile({ id, file }: { id: string; file: File }) {
  const formData = new FormData();
  formData.append('file', file);
  
  return {
    url: `${endpointUrl}/${id}/upload`,
    method: 'POST',
    body: formData
  };
}

function deleteUpload({ id }: { id: string }) {
  return {
    url: `${endpointUrl}/${id}/upload`,
    method: 'DELETE'
  };
}

function cancelVacancy({ id, reason }: { id: string; reason: string }) {
  return {
    url: `${endpointUrl}/${id}/cancel`,
    method: 'DELETE',
    body: { reason }
  };
}

function reopenVacancy({ id }: { id: string }) {
  return {
    url: `${endpointUrl}/${id}/reopen`,
    method: 'PATCH'
  };
}

function exportToExcel() {
  return {
    url: `${endpointUrl}/export/excel`,
    method: 'POST',
    responseHandler: async (response: Response) => {
      return response.blob();
    }
  };
}

export const vacancyApiSlice = apiSlice.injectEndpoints({
  endpoints: ({ query, mutation }) => ({
    getAllVacancies: query<Vacancy[], void>({
      query: getVacancies,
      providesTags: ['Vacancies']
    }),
    getVacancy: query<Vacancy, { id: string }>({
      query: getVacancy,
      providesTags: ['Vacancies']
    }),
    getVacancyHistory: query<VacancyHistory[], { id: string }>({
      query: getVacancyHistory,
      providesTags: ['Vacancies']
    }),
    createVacancy: mutation<Vacancy, SaveVacancyDto>({
      query: createVacancy,
      invalidatesTags: ['Vacancies']
    }),
    updateVacancy: mutation<void, { id: string } & SaveVacancyDto>({
      query: updateVacancy,
      invalidatesTags: ['Vacancies']
    }),
    updateUserVacancy: mutation<void, { id: string; userId: string | null }>({
      query: updateUserVacancy,
      invalidatesTags: ['Vacancies']
    }),
    updateStatusVacancy: mutation<void, { id: string; status: string }>({
      query: updateStatusVacancy,
      invalidatesTags: ['Vacancies']
    }),
    updateVacancyCandidate: mutation<void, { id: string } & UpdateVacancyCandidateDto>({
      query: updateVacancyCandidate,
      invalidatesTags: ['Vacancies']
    }),
    searchVacancies: mutation<Vacancy[], SearchVacancyDto>({
      query: searchVacancies,
      invalidatesTags: ['Vacancies']
    }),
    uploadFile: mutation<{ message: string; router: string }, { id: string; file: File }>({
      query: uploadFile,
      invalidatesTags: ['Vacancies']
    }),
    deleteUpload: mutation<{ message: string }, { id: string }>({
      query: deleteUpload,
      invalidatesTags: ['Vacancies']
    }),
    cancelVacancy: mutation<{ message: string }, { id: string; reason: string }>({
      query: cancelVacancy,
      invalidatesTags: ['Vacancies']
    }),
    reopenVacancy: mutation<void, { id: string }>({
      query: reopenVacancy,
      invalidatesTags: ['Vacancies']
    }),
    exportToExcel: mutation<Blob, void>({
      query: exportToExcel
    })
  })
});

export const {
  useGetAllVacanciesQuery,
  useGetVacancyQuery,
  useGetVacancyHistoryQuery,
  useCreateVacancyMutation,
  useUpdateVacancyMutation,
  useUpdateUserVacancyMutation,
  useUpdateStatusVacancyMutation,
  useUpdateVacancyCandidateMutation,
  useSearchVacanciesMutation,
  useUploadFileMutation,
  useDeleteUploadMutation,
  useCancelVacancyMutation,
  useReopenVacancyMutation,
  useExportToExcelMutation
} = vacancyApiSlice;

export default vacancyApiSlice;
